import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
    name: 'geojsonToPaths',
    standalone: false,
})
export class GeojsonToPathsPipe implements PipeTransform {
    /**
     * Converts GeoJSON polygon coordinates to Google Maps LatLngLiteral paths
     * @param geojson The GeoJSON object with type 'Polygon' and coordinates
     * @returns Array of paths suitable for google-map polygon component.
     *          First path is the outer ring (clockwise), subsequent paths are holes/cutouts (counter-clockwise).
     */
    transform(geojson: any): google.maps.LatLngLiteral[][] | null {
        if (!geojson || geojson.type !== 'Polygon' || !geojson.coordinates) {
            return null;
        }

        // GeoJSON coordinates are [lng, lat], Google Maps expects {lat, lng}
        // coordinates[0] is outer ring, coordinates[1+] are holes
        return geojson.coordinates.map((ring: number[][], index: number) => {
            const path = ring.map(([lng, lat]) => ({ lat, lng }));

            // Ensure correct winding order for Google Maps:
            // - Outer ring (index 0) must be clockwise
            // - Holes (index > 0) must be counter-clockwise
            if (index === 0) {
                return this.ensureClockwise(path);
            }
            return this.ensureCounterClockwise(path);
        });
    }

    private ensureClockwise(coordinates: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        if (coordinates.length < 3) return coordinates;
        const signedArea = this.calculateSignedArea(coordinates);
        // If area is positive, it's counter-clockwise, so reverse it
        return signedArea > 0 ? coordinates.slice().reverse() : coordinates;
    }

    private ensureCounterClockwise(coordinates: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        if (coordinates.length < 3) return coordinates;
        const signedArea = this.calculateSignedArea(coordinates);
        // If area is negative, it's clockwise, so reverse it
        return signedArea < 0 ? coordinates.slice().reverse() : coordinates;
    }

    private calculateSignedArea(coordinates: google.maps.LatLngLiteral[]): number {
        if (coordinates.length < 3) return 0;
        let area = 0;
        for (let i = 0; i < coordinates.length; i++) {
            const j = (i + 1) % coordinates.length;
            area += coordinates[i].lat * coordinates[j].lng;
            area -= coordinates[j].lat * coordinates[i].lng;
        }
        return area / 2;
    }
}
